import { useState, useEffect, useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Practitioner, Patient, PractitionerRole } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { useDebouncedValue } from '@mantine/hooks';
import { ARKON } from '@consts/systems.consts';

//------------------------------------------------------------------------------
export const useExternalDoctors = () => {
  const medplum = useMedplum();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
  const [externalDoctors, setExternalDoctors] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  //----------------------------------------------------------------------------
  const fetchExternalDoctors = useCallback(
    async (query = '') => {
      if (query.trim().length < 2) {
        setExternalDoctors([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Search for practitioners with the external organization extension
        const searchParams: Record<string, string> = {
          _count: '100',
          name: query,
        };

        const allPractitioners = await medplum.searchResources(
          'Practitioner',
          searchParams
        );

        // Filter for those with a non-internal organization tag
        const practitioners = allPractitioners.filter((practitioner) =>
          practitioner.meta?.tag?.some(
            (tag) =>
              tag.system === ARKON.CS.PRACTITIONER.ORGANIZATION.BASE &&
              tag.code !== ARKON.CS.PRACTITIONER.ORGANIZATION.CODES.INTERNAL
          )
        );

        setExternalDoctors(practitioners);
      } catch (err) {
        console.error('Error fetching external doctors:', err);
        setError('Failed to fetch external doctors');
      } finally {
        setLoading(false);
      }
    },
    [medplum]
  );
  //----------------------------------------------------------------------------
  // Load external doctors on mount and when search changes
  useEffect(() => {
    fetchExternalDoctors(debouncedSearch);
  }, [debouncedSearch, fetchExternalDoctors]);
  //----------------------------------------------------------------------------
  const refreshExternalDoctors = useCallback(() => {
    fetchExternalDoctors(debouncedSearch);
  }, [fetchExternalDoctors, debouncedSearch]);

  //----------------------------------------------------------------------------
  // Helper function to get organization name from practitioner
  const getOrganizationName = useCallback(
    (practitioner: Practitioner): string => {
      const organizationTag = practitioner.meta?.tag?.find(
        (tag) => tag.system === ARKON.CS.PRACTITIONER.ORGANIZATION.BASE
      );
      return organizationTag?.display || 'External';
    },
    []
  );

  //----------------------------------------------------------------------------
  // Helper function to filter by organization
  const filterByOrganization = useCallback(
    (organizationName: string): Practitioner[] => {
      return externalDoctors.filter((doctor) =>
        getOrganizationName(doctor)
          .toLowerCase()
          .includes(organizationName.toLowerCase())
      );
    },
    [externalDoctors, getOrganizationName]
  );

  //----------------------------------------------------------------------------  // Helper function to search by name
  const searchByName = useCallback(
    (searchTerm: string): Practitioner[] => {
      if (!searchTerm.trim()) return externalDoctors;

      const term = searchTerm.toLowerCase();
      return externalDoctors.filter((doctor) => {
        const fullName =
          `${doctor.name?.[0]?.given?.[0] || ''} ${doctor.name?.[0]?.family || ''}`.toLowerCase();
        return fullName.includes(term);
      });
    },
    [externalDoctors]
  );
  //----------------------------------------------------------------------------
  // Helper function to clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  //----------------------------------------------------------------------------
  // Helper function to assign external doctor to patient
  const assignExternalDoctorToPatient = useCallback(
    async (
      patient: Patient,
      practitioner: Practitioner,
      role: 'assigned' | 'referral' = 'referral'
    ) => {
      if (!patient || !practitioner.id) {
        throw new Error('Patient and practitioner are required');
      }

      try {
        // Create a PractitionerRole resource to link the external doctor to the patient
        const practitionerRole: PractitionerRole = {
          resourceType: 'PractitionerRole',
          practitioner: {
            reference: `Practitioner/${practitioner.id}`,
            display: formatHumanName(practitioner.name?.[0]),
          },
          code: [
            {
              coding: [
                {
                  system: ARKON.CS.PRACTITIONER.ROLE.BASE,
                  code: role,
                  display:
                    role === ARKON.CS.PRACTITIONER.ROLE.CODES.REFERRAL
                      ? 'Referral Doctor'
                      : 'Assigned Doctor',
                },
              ],
            },
          ],
          identifier: [
            {
              system: 'patient-assignment',
              value: patient.id,
            },
          ],
          active: true,
        };

        const createdRole = await medplum.createResource(practitionerRole);

        // Refresh the external doctors list to reflect any changes
        await refreshExternalDoctors();

        return createdRole;
      } catch (error) {
        console.error('Error assigning external doctor to patient:', error);
        throw error;
      }
    },
    [medplum, refreshExternalDoctors]
  );
  //----------------------------------------------------------------------------
  return {
    searchQuery,
    debouncedSearch,
    setSearchQuery,
    externalDoctors,
    loading,
    error,
    refreshExternalDoctors,
    getOrganizationName,
    filterByOrganization,
    searchByName,
    clearSearch,
    assignExternalDoctorToPatient,
  };
};
